import React from 'react';

import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';

import RoomRateRoomRow, { getReadOnlyRatePlanLabels } from './RoomRateRoomRow';
import type { RoomRateRatePlanOption } from './RoomRateRoomRow';
import type { RoomRateRoom } from './utils';

interface Props {
  appliedRatePlanNamesByRoomTypeId: Record<string, Array<string>>;
  disabled: boolean;
  onClearRooms: () => void;
  onSelectAllRooms: () => void;
  onSelectRatePlans: (roomTypeId: string, ratePlanIds: Array<string>) => void;
  ratePlanOptions: Array<RoomRateRatePlanOption>;
  readOnly?: boolean;
  rooms: Array<RoomRateRoom>;
  selectedRatePlanIds: Record<string, Array<string>>;
}

function getRatePlanSummary(ratePlans: Array<RoomRateRatePlanOption>) {
  if (ratePlans.length === 0) {
    return 'the new Flash rate plans';
  }

  if (ratePlans.length === 1) {
    return ratePlans[0].name || 'the new Flash rate plan';
  }

  return `${ratePlans.length} Flash rate plans`;
}

export default function RoomRatesStep({
  appliedRatePlanNamesByRoomTypeId,
  disabled,
  onClearRooms,
  onSelectAllRooms,
  onSelectRatePlans,
  ratePlanOptions,
  readOnly = false,
  rooms,
  selectedRatePlanIds,
}: Props) {
  const selectableRooms = rooms.filter((room) => !room.hasRatePlan);
  const selectedRoomCount = readOnly
    ? rooms.filter(
        (room) =>
          getReadOnlyRatePlanLabels(room.id, appliedRatePlanNamesByRoomTypeId, ratePlanOptions, selectedRatePlanIds)
            .length > 0,
      ).length
    : Object.keys(selectedRatePlanIds).length;
  const allSelectableRoomsSelected =
    selectableRooms.length > 0 && selectableRooms.every((room) => selectedRatePlanIds[room.id]?.length > 0);
  const ratePlanSummary = getRatePlanSummary(ratePlanOptions);

  if (ratePlanOptions.length === 0) {
    return <Alert severity="error">Create at least one Flash rate plan before applying room rates.</Alert>;
  }

  if (rooms.length === 0) {
    return <Alert severity="warning">No rooms are available for this property.</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {readOnly
          ? `${ratePlanSummary} have already been applied. Review the assignments below, or continue to the next step.`
          : 'Choose which Flash rate plans to apply to each room. All rate plans are selected by default.'}
      </Alert>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.25, bgcolor: 'grey.50' }}
        >
          <Stack spacing={0.25}>
            <Typography variant="subtitle2">Rooms</Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedRoomCount} of {readOnly ? rooms.length : selectableRooms.length} rooms selected
            </Typography>
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                size="small"
                onClick={onSelectAllRooms}
                disabled={disabled || selectableRooms.length === 0 || allSelectableRoomsSelected}
              >
                Select all rooms
              </Button>
              <Button variant="text" size="small" onClick={onClearRooms} disabled={disabled || selectedRoomCount === 0}>
                Clear
              </Button>
            </Stack>
          )}
        </Stack>
        <Divider />
        {rooms.map((room) => (
          <RoomRateRoomRow
            key={room.id}
            appliedRatePlanNamesByRoomTypeId={appliedRatePlanNamesByRoomTypeId}
            disabled={disabled}
            onSelectRatePlans={onSelectRatePlans}
            ratePlanOptions={ratePlanOptions}
            readOnly={readOnly}
            room={room}
            selectedRatePlanIds={selectedRatePlanIds}
          />
        ))}
      </Box>
    </Stack>
  );
}
